import {memo, useEffect, useMemo, useState, type ReactNode} from 'react'
import {Heading, IconButton, VisuallyHidden} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {XIcon} from '@primer/octicons-react'
import {isModernPullRequestExperienceEnabled} from '@github-ui/pull-request-feature-flags'

import {
  DockedPanel,
  useActiveDockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useIsDockedPanelCompact,
} from '../../contexts/DockedPanelContext'
import {
  CompactPanelBodyClassNameContext,
  CompactPanelFooterPresenceContext,
  CompactPanelSlotsContext,
  type CompactPanelSlotTargets,
} from './CompactPanel'
import {PanelNavigationDropdown} from './PanelNavigationDropdown'
import {restorePanelFocusAfterClose} from './panel-focus'
import {getPanelHeading, getPanelLabelName} from './panel-registry'
import styles from './Panel.module.css'

interface CompactSidePanelDialogProps {
  /**
   * The subtree containing the panels that may mount in compact mode
   * (typically `DockedSidePanelContent`). The provider wraps this subtree
   * so panels' `CompactPanel.*` portals can find the targets owned by this
   * shell. When `updated_pull_request_header` is OFF the shell is suppressed
   * and the subtree renders unchanged (each panel uses its legacy compact
   * `<Dialog>` wrapper instead).
   */
  children?: ReactNode
}

/**
 * Single Dialog shell hosting whichever panel is active in compact mode.
 *
 * Mounted once at the layout level wrapping `DockedSidePanelContent`. While
 * `isCompact && activePanel != null` it keeps one `<Dialog>` mounted with a
 * fixed header (`PanelNavigationDropdown` + close button). The active panel
 * portals its content into one of four regions via the `CompactPanel.*`
 * components: `Content` (body), `Header` (secondary header bar below the
 * title row), `HeaderActions` (next to the close button), and `Footer`
 * (sticky `Dialog.Footer`, rendered only when the active panel actually
 * mounts a `CompactPanel.Footer`). Switching panels does not unmount the
 * Dialog, so we avoid the close-animation / focus-restore / scroll-lock
 * flicker that per-panel Dialogs caused.
 *
 * The provider tree + `children` are always rendered (regardless of
 * breakpoint or flag) so this component never remounts the panel
 * subtree on its own — only the `<Dialog>` itself mounts/unmounts when
 * `shouldRenderDialog` flips. Note that each panel still returns a
 * different root JSX tree (`Panel.*` vs `Dialog` vs `CompactPanel.*`
 * portals) based on `isCompact`, so crossing the compact breakpoint
 * still remounts the panel's child subtree; only state declared
 * directly on the panel function itself (e.g. `commentText`,
 * `activeTab`) survives.
 */
export const CompactSidePanelDialog = memo(function CompactSidePanelDialogInner({
  children,
}: CompactSidePanelDialogProps) {
  const activePanel = useActiveDockedPanel()
  const isCompact = useIsDockedPanelCompact()

  // The shared shell is gated on `updated_pull_request_header`. When the flag
  // is OFF, each panel renders its own legacy compact `<Dialog>` wrapper.
  const shouldRenderDialog = isCompact && activePanel != null && isModernPullRequestExperienceEnabled()

  // The four portal slots live in a single `useState` record so panels
  // re-render once any target is attached. Ref callbacks below populate
  // one field each as the Dialog mounts; when the Dialog unmounts the
  // ref callbacks run with null and the slots flip back to null.
  const [slots, setSlots] = useState<CompactPanelSlotTargets>({
    body: null,
    header: null,
    headerTitle: null,
    headerActions: null,
    footer: null,
  })
  // Stable per-slot ref callbacks. We memoize once so React doesn't tear
  // down/re-attach refs (and trigger setSlots churn) on every render.
  const {bodyRef, headerRef, headerTitleRef, headerActionsRef, footerRef} = useMemo(() => {
    const makeRef = (key: keyof CompactPanelSlotTargets) => (el: HTMLElement | null) => {
      setSlots(prev => (prev[key] === el ? prev : {...prev, [key]: el}))
    }
    return {
      bodyRef: makeRef('body'),
      headerRef: makeRef('header'),
      headerTitleRef: makeRef('headerTitle'),
      headerActionsRef: makeRef('headerActions'),
      footerRef: makeRef('footer'),
    }
  }, [])

  // Active panel's `CompactPanel.Content` may publish a className that's
  // applied to `Dialog.Body` (e.g. `p-0` for flush panels).
  const [bodyClassName, setBodyClassName] = useState<string | undefined>(undefined)
  const bodyClassNameContextValue = useMemo(() => ({setBodyClassName}), [])

  // Drive `Dialog.Footer` rendering from whether the active panel's
  // `CompactPanel.Footer` is mounted (rather than a static registry).
  // Otherwise panels that conditionally omit their footer (e.g. Comments
  // when commenting is disabled) would leave an empty 16px footer bar
  // with a divider.
  const [hasFooter, setHasFooter] = useState(false)
  const footerPresenceContextValue = useMemo(() => ({setHasFooter}), [])

  return (
    <CompactPanelSlotsContext value={slots}>
      <CompactPanelBodyClassNameContext value={bodyClassNameContextValue}>
        <CompactPanelFooterPresenceContext value={footerPresenceContextValue}>
          {children}
          {shouldRenderDialog && (
            <CompactSidePanelDialogShell
              activePanel={activePanel}
              bodyClassName={bodyClassName}
              hasFooter={hasFooter}
              bodyRef={bodyRef}
              headerRef={headerRef}
              headerTitleRef={headerTitleRef}
              headerActionsRef={headerActionsRef}
              footerRef={footerRef}
            />
          )}
        </CompactPanelFooterPresenceContext>
      </CompactPanelBodyClassNameContext>
    </CompactPanelSlotsContext>
  )
})

interface CompactSidePanelDialogShellProps {
  activePanel: DockedPanel
  bodyClassName: string | undefined
  hasFooter: boolean
  bodyRef: (el: HTMLElement | null) => void
  headerRef: (el: HTMLElement | null) => void
  headerTitleRef: (el: HTMLElement | null) => void
  headerActionsRef: (el: HTMLElement | null) => void
  footerRef: (el: HTMLElement | null) => void
}

function CompactSidePanelDialogShell({
  activePanel,
  bodyClassName,
  hasFooter,
  bodyRef,
  headerRef,
  headerTitleRef,
  headerActionsRef,
  footerRef,
}: CompactSidePanelDialogShellProps) {
  const {closePanel} = useDockedPanelActions()
  const {closeButtonRef, returnFocusRef} = useDockedPanelFocusRefs()

  const handleClose = () => {
    restorePanelFocusAfterClose(returnFocusRef.current)
    closePanel()
  }

  // Move focus to the close button on every panel switch so focus does not
  // get stranded on a body element that just unmounted. Initial focus is
  // also covered by Dialog's `initialFocusRef`.
  useEffect(() => {
    closeButtonRef.current?.focus()
  }, [activePanel, closeButtonRef])

  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={returnFocusRef}
      onClose={handleClose}
      renderFooter={
        hasFooter
          ? () => (
              <Dialog.Footer>
                <div ref={footerRef} className="width-full" />
              </Dialog.Footer>
            )
          : () => null
      }
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header>
          {/* Stable accessible name for the dialog, independent of the
              visible title (which is an interactive dropdown). */}
          <VisuallyHidden id={dialogLabelId}>{getPanelHeading(activePanel)}</VisuallyHidden>
          <div className={styles.CompactHeaderContent}>
            <Heading as="h1" className={styles.Heading}>
              <PanelNavigationDropdown />
              <span ref={headerTitleRef} className="d-flex flex-1 min-width-0" />
            </Heading>
            <div className={styles.HeaderActions}>
              <div className="d-flex flex-items-center" ref={headerActionsRef} />
              <IconButton
                ref={closeButtonRef}
                aria-label={`Close ${getPanelLabelName(activePanel)} panel`}
                icon={XIcon}
                variant="invisible"
                onClick={handleClose}
              />
            </div>
          </div>
          <div ref={headerRef} />
        </Dialog.Header>
      )}
      renderBody={({children: bodyChildren}) => <Dialog.Body className={bodyClassName}>{bodyChildren}</Dialog.Body>}
    >
      <div className="d-flex flex-column flex-1 min-height-0" ref={bodyRef} />
    </Dialog>
  )
}
